// The builder's state, in one small external store so later phases (the gate's capture
// call, the pack) read the same inputs the visitor typed. Pure: the Worker imports it to
// render the island's first paint, so nothing here may touch the DOM.

import {
  BUSINESS_TYPES,
  CATEGORIES,
  PLATFORMS,
  getTemplate,
  templatesInCategory,
  type BusinessType,
  type Category,
  type FillInputs,
  type Platform,
  type PromptTemplate,
  type SlotKey,
} from "../../shared/prompts/templates";
import type { CaptureField } from "../../shared/prompts/capture-contract";

export type ExtraSlot = Exclude<SlotKey, "brand" | "audience" | "platform">;

export interface BuilderInputs {
  goal: Category;
  platform: Platform;
  businessType: BusinessType | "";
  brand: string;
  audience: string;
  tone: string;
  pillars: string;
  promptId: string;
  /** Values for the template-specific slots, kept across prompts. */
  slots: Partial<Record<ExtraSlot, string>>;
}

export interface GateFields {
  email: string;
  /** A RoleId from shared/prompts/gate-options, or "" before a choice. */
  role: string;
  /** An AccountCountId, or "". */
  accounts: string;
  newsletter: boolean;
}

/** What the gate shows after a check or a submit. Messages are deck copy, ready to render. */
export interface GateFeedback {
  submitting: boolean;
  fieldErrors: Partial<Record<CaptureField, string>>;
  formError: string | null;
  /** The free-mail nudge: shown, never blocking. */
  nudge: boolean;
  /** Double opt-in: the address the link went to. Set, the gate shows "check your inbox". */
  sentTo: string | null;
  /** When the last link was sent, for the resend wait. */
  sentAt: number | null;
  /** The last send was a resend. */
  resent: boolean;
}

export interface BuilderState {
  inputs: BuilderInputs;
  showMore: boolean;
  gateOpen: boolean;
  gate: GateFields;
  feedback: GateFeedback;
}

export const EMPTY_FEEDBACK: GateFeedback = {
  submitting: false,
  fieldErrors: {},
  formError: null,
  nudge: false,
  sentTo: null,
  sentAt: null,
  resent: false,
};

/** Seconds between sends of the confirmation link, on the gate's resend control. */
export const RESEND_WAIT_SECONDS = 60;

export type PageType = "hub" | "category" | "lp";

/** What the server hands the island, as JSON on the mount point. */
export interface BuilderSeed {
  page: PageType;
  goal: Category;
  promptId: string;
  platform: Platform;
  /** Paid landing pages only: the ad hook, for the view event, and a prefilled business type. */
  hook?: string;
  businessType?: BusinessType;
}

export function isCategory(value: unknown): value is Category {
  return typeof value === "string" && (CATEGORIES as readonly string[]).includes(value);
}

export function isPlatform(value: unknown): value is Platform {
  return typeof value === "string" && (PLATFORMS as readonly string[]).includes(value);
}

export function isBusinessType(value: unknown): value is BusinessType {
  return typeof value === "string" && (BUSINESS_TYPES as readonly string[]).includes(value);
}

/**
 * A valid seed for a page, honouring a requested prompt id when it exists. A category page
 * keeps its own goal, so a prompt from another goal is ignored there.
 */
export function makeSeed(page: PageType, goal: Category, requestedPromptId?: string): BuilderSeed {
  const found = requestedPromptId ? getTemplate(requestedPromptId) : undefined;
  const requested = page === "category" && found?.category !== goal ? undefined : found;
  const template = requested ?? templatesInCategory(goal)[0];
  return {
    page,
    goal: template.category,
    promptId: template.id,
    platform: template.platforms[0] ?? PLATFORMS[0],
  };
}

/** The builder's query parameters. Without JS, its Generate button submits them as a GET form. */
export interface SeedRequest {
  prompt?: string;
  goal?: string;
  platform?: string;
}

/**
 * makeSeed plus the goal and platform the no-JS form sends. A requested goal wins over a
 * prompt from another goal, so changing the goal and pressing Generate shows that goal.
 */
export function seedFromRequest(
  page: PageType,
  goal: Category,
  request: SeedRequest = {},
  defaults: { promptId?: string; platform?: Platform } = {},
): BuilderSeed {
  const requestedGoal = isCategory(request.goal) ? request.goal : undefined;
  const wanted = request.prompt ?? defaults.promptId;
  const prompt = wanted ? getTemplate(wanted) : undefined;
  const promptId = requestedGoal && prompt?.category !== requestedGoal ? undefined : wanted;
  const seed = makeSeed(page, requestedGoal ?? goal, promptId);
  const platform = isPlatform(request.platform) ? request.platform : defaults.platform;
  if (platform) seed.platform = platform;
  return seed;
}

/** Parses the seed JSON from the mount point, falling back to the hub default. */
export function parseSeed(raw: string | null | undefined): BuilderSeed {
  let data: Record<string, unknown> = {};
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === "object") data = parsed as Record<string, unknown>;
  } catch {
    data = {};
  }
  const page: PageType = data.page === "category" || data.page === "lp" ? data.page : "hub";
  const goal = isCategory(data.goal) ? data.goal : CATEGORIES[0];
  const seed: BuilderSeed = makeSeed(
    page,
    goal,
    typeof data.promptId === "string" ? data.promptId : undefined,
  );
  if (isPlatform(data.platform)) seed.platform = data.platform;
  if (typeof data.hook === "string" && /^[a-z0-9-]{1,40}$/.test(data.hook)) seed.hook = data.hook;
  if (isBusinessType(data.businessType)) seed.businessType = data.businessType;
  return seed;
}

export function initialState(seed: BuilderSeed): BuilderState {
  return {
    inputs: {
      goal: seed.goal,
      platform: seed.platform,
      businessType: seed.businessType ?? "",
      brand: "",
      audience: "",
      tone: "",
      pillars: "",
      promptId: seed.promptId,
      slots: {},
    },
    showMore: false,
    gateOpen: false,
    gate: { email: "", role: "", accounts: "", newsletter: true },
    feedback: EMPTY_FEEDBACK,
  };
}

/** The template the inputs point at, falling back to the goal's first prompt. */
export function currentTemplate(inputs: BuilderInputs): PromptTemplate {
  const chosen = getTemplate(inputs.promptId);
  if (chosen && chosen.category === inputs.goal) return chosen;
  return templatesInCategory(inputs.goal)[0];
}

/** The visitor's raw value for a slot: brand and audience sit on inputs, the rest in slots. */
export function slotValueOf(inputs: BuilderInputs, slot: Exclude<SlotKey, "platform">): string {
  if (slot === "brand" || slot === "audience") return inputs[slot];
  return inputs.slots[slot] ?? "";
}

export function fillInputsFor(inputs: BuilderInputs): FillInputs {
  return {
    businessType: inputs.businessType || undefined,
    platform: inputs.platform,
    values: { ...inputs.slots, brand: inputs.brand, audience: inputs.audience },
    tone: inputs.tone,
    pillars: inputs.pillars,
  };
}

export interface BuilderStore {
  get(): BuilderState;
  set(update: (state: BuilderState) => BuilderState): void;
  subscribe(listener: () => void): () => void;
}

export function createBuilderStore(initial: BuilderState): BuilderStore {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(update) {
      const next = update(state);
      if (next === state) return;
      state = next;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
